import React from 'react';
import {
  Breadcrumbs,
  Grid,
  Link,
  PageHeader,
  Typography,
  Stack,
  ShipsIcon,
  StorageIcon,
  TrendingUpIcon
} from '@esgian/esgianui';
import { useNavigate } from 'react-router-dom';
import { fetchVesselStats } from '@api';
import OperatorsOverviewSection from '@components/Sections/ShipAnalytics/OperatorsOverviewSection';
import { DATE_FORMAT } from '@constants';
import { useLookupOperatorsQuery, useSegment } from '@hooks';
import { CommercialCategoryTypeSelect, OperatorTypeSelect } from '@components/Inputs';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment/moment';
import KpiBox from '@components/Display/KpiBox';
import { formatNumberReadable, parseNumberToSignificant } from '@helpers';

function OperatorsOverviewPage() {
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const operatorType = useSelector(getOperatorType);
  const { segment, isRoRo, uriExt } = useSegment();
  const navigate = useNavigate();
  const lookupOperatorsQuery = useLookupOperatorsQuery();

  const routeChange = (path) => {
    navigate(path);
  };
  const getOverviewTableData = (response) => {
    let operatorsData = [...response].slice(1);
    let res = [];

    operatorsData.forEach(
      ({
        operatorLongName,
        operatorShortName,
        countryName,
        countryId,
        isoAlpha2,
        operatorId,
        vesselTimeSeries
      }) => {
        const { ageInformation, capacity, numVessels } = vesselTimeSeries?.[12] ?? {};

        res.push({
          operatorLongName,
          operatorShortName,
          countryName,
          countryId,
          countryCode: isoAlpha2,
          operatorId,
          avgAge: ageInformation?.averageAge,
          fleetSize: numVessels,
          avgCapacity: capacity?.[isRoRo ? 'averageCEU' : 'averageDeadweightTonnage'],
          totalCapacity: capacity?.[isRoRo ? 'totalCEU' : 'totalDeadweightTonnage']
        });
      }
    );
    return res;
  };

  const operatorsOverviewQuery = useQuery({
    queryKey: [
      'vesselStats',
      'allOpr',
      '1year',
      {
        segmentIds: [segment.id],
        operatorIds: lookupOperatorsQuery?.data?.map((operator) => operator.id),
        operatorTypeIds: isRoRo ? [operatorType] : [],
        commercialCategoryIds: isRoRo ? [] : [commercialCategoryType],
        startDate: moment().subtract(1, 'year').startOf('month').format(DATE_FORMAT),
        endDate: moment().format(DATE_FORMAT)
      }
    ],
    enabled: lookupOperatorsQuery.data?.length > 0,
    placeholderData: { kpiStats: null, overviewTableData: [] },
    queryFn: ({ queryKey, signal }) => {
      return fetchVesselStats(signal, queryKey[3])
        .then((response) => {
          if (!response.length) return { kpiStats: null, overviewTableData: [] };
          const kpiObj = response[0]; // BE returns aggregated in index 0
          const kpiData = kpiObj?.vesselTimeSeries?.[12] || {};
          const { capacity = {}, ageInformation = {}, numVessels: numVesselsKpi } = kpiData;

          const tableData = getOverviewTableData(response);
          return {
            kpiStats: {
              fleetSize: numVesselsKpi ?? 0,
              averageAge: ageInformation.averageAge ?? 0,
              averageCapacity: isRoRo
                ? capacity.averageCEU
                : capacity.averageDeadweightTonnage ?? 0,
              totalCapacity: isRoRo ? capacity.totalCEU : capacity.totalDeadweightTonnage ?? 0
            },
            overviewTableData: tableData
          };
        })
        .catch(() => {
          return { kpiStats: null, overviewTableData: [] };
        });
    }
  });

  const { fleetSize, averageAge, averageCapacity, totalCapacity } =
    operatorsOverviewQuery.data.kpiStats || {};
  return (
    <Grid item container spacing={2}>
      <Grid item>
        <PageHeader
          id={'lbl-operator-overview'}
          title={'Operators Overview'}
          breadcrumbs={
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                color="inherit"
                id="home-breadcrumb"
                sx={{ cursor: 'pointer' }}
                onClick={() => routeChange(`${uriExt}/commercial-analytics`)}>
                Home
              </Link>
              <Link
                sx={{ cursor: 'pointer' }}
                underline="hover"
                color="inherit"
                id="ship-analysis-breadcrumb"
                onClick={() => routeChange(`${uriExt}/commercial-analytics`)}>
                Commercial Analytics
              </Link>
              <Typography color="primary" id="operator-overview">
                Operators Overview
              </Typography>
            </Breadcrumbs>
          }
        />
      </Grid>

      <Grid item xs={12}>
        {isRoRo ? <OperatorTypeSelect /> : <CommercialCategoryTypeSelect />}
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={operatorsOverviewQuery.isFetching}
            icon={<ShipsIcon />}
            value={fleetSize ? fleetSize : ''}
            title={'Fleet Size'}
            subtitle={'Vessels'}
          />
          <KpiBox
            loading={operatorsOverviewQuery.isFetching}
            icon={<TrendingUpIcon />}
            value={averageAge ? parseNumberToSignificant(averageAge) : ''}
            title={'Average Age'}
            subtitle={'Years'}
          />
          <KpiBox
            loading={operatorsOverviewQuery.isFetching}
            icon={<StorageIcon />}
            value={averageCapacity ? formatNumberReadable(parseInt(averageCapacity)) : '-'}
            title={'Average Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
          <KpiBox
            loading={operatorsOverviewQuery.isFetching}
            icon={<StorageIcon />}
            value={totalCapacity ? formatNumberReadable(totalCapacity) : ''}
            title={'Total Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <OperatorsOverviewSection
          overviewData={operatorsOverviewQuery.data.overviewTableData}
          overviewDataLoading={operatorsOverviewQuery.isFetching}
        />
      </Grid>
    </Grid>
  );
}

OperatorsOverviewPage.propTypes = {};

OperatorsOverviewPage.defaultProps = {};

export default OperatorsOverviewPage;
